import { useId, useLayoutEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent } from "react";
import { Link } from "react-router-dom";
import { numero } from "../lib/admin";
import { contracaoDaHora, horaComArtigo } from "../lib/horaNoTexto";
import {
  ateOHorizonte,
  metaLegivel,
  quandoLegivel,
  notaLegivel,
  rotuloCurtoDoBalde,
  rotuloDoEstadoDoBalde,
  rotuloLongoDoBalde,
  type Balde,
  type Granularidade,
} from "../lib/avaliacoes";
import { ChaveDaSerie, Marcador, corDaSerie, type Posicao } from "./AdminAvaliacoesUi";

/**
 * Os gráficos do painel das avaliações (000328), num lugar só: a Visão geral, a
 * página de uma ferramenta e o Comparar desenham com as MESMAS peças. Duas
 * cópias do eixo, da dica e da tabela divergiriam, e a que divergisse em
 * silêncio seria a que ninguém olha.
 *
 * Desenhados à mão, em SVG e HTML, e sem biblioteca de gráfico: a área do aluno
 * não carrega recharts (o cabeçalho do `index.css` diz por quê), e o painel
 * mora no mesmo bundle.
 *
 * ZERO conta aqui. As médias, as contagens e os baldes do eixo do tempo vêm
 * prontos do banco (`av_admin_series`, `av_admin_visao_geral`); o que mora aqui
 * é a ESCALA — onde cada número cai na tela —, o eixo, a dica e a tabela.
 *
 * As decisões que vieram do `Grafico` da comparação da 000327, cada uma paga:
 *   · o tamanho REAL do espaço, medido por `ResizeObserver`, e não um `viewBox`
 *     esticado: esticado, o texto do eixo encolhe junto e vira ilegível no
 *     celular;
 *   · o eixo das NOTAS com zoom. Linha codifica POSIÇÃO, não comprimento — com
 *     as médias entre 8,8 e 9,7 (medido em 29/09), um eixo de 0 a 10 poria todas
 *     as linhas nos últimos 20% da altura. Coluna e barra, que codificam
 *     COMPRIMENTO, começam no zero;
 *   · a linha QUEBRA no balde sem avaliação, em vez de ligar os vizinhos como se
 *     o meio tivesse existido;
 *   · o rótulo do fim de cada linha só aparece se nenhum encosta noutro, nem no
 *     da meta — empilhar rótulos que colidem os separa das linhas deles;
 *   · a dica lista TODAS as séries do balde, e a área do ponteiro é o gráfico
 *     inteiro: quem aponta mira um dia, nunca uma linha de 2px;
 *   · o teclado percorre os baldes (setas, Home, End), e a tabela dentro do
 *     `<details>` tem os mesmos números — o gráfico enfeita, a tabela garante.
 * E as que nasceram aqui estão escritas onde a decisão é tomada: o ponto VAZADO
 * (`POUCAS`), os marcadores num eixo apertado, o passo dos rótulos do tempo, a
 * contagem única na dica e o anúncio ao leitor de tela.
 *
 * Cores: só tokens (`var(--cf-…)`), que trocam com o tema e já foram medidos
 * (a tabela está no `index.css`). As séries têm cor e FORMA (`Marcador`); a
 * marca de série única é o `acento`; texto nunca usa a cor da série.
 */

/** Abaixo disto, o ponto é desenhado VAZADO: uma média de 3 avaliações parece uma queda e é uma pessoa. */
export const POUCAS = 5;

export type SerieDoGrafico = {
  /** Estável: o slug da ferramenta, ou o campo ("media_geral", "utilidade"…). */
  id: string;
  rotulo: string;
  /** A posição decide a cor e a forma (1 a 4) — e acompanha a série, nunca a ordem. */
  p: Posicao;
  /** Alinhado a `baldes`: um valor por balde, nulo onde não houve avaliação. */
  valores: (number | null)[];
  /** Alinhado a `baldes`: quantas avaliações sustentam cada valor (nulo no balde que ainda não chegou). */
  contagens: (number | null)[];
};

export type PropsDoGraficoDeLinhas = {
  baldes: Balde[];
  granularidade: Granularidade;
  series: SerieDoGrafico[];
  /** As metas, tracejadas, com o rótulo na margem direita. */
  metas?: { valor: number; rotulo: string }[];
  /** A frase que o leitor de tela ouve no lugar do desenho. */
  descricao: string;
  /** O texto quando nenhuma série tem ponto. */
  semPontos: string;
  /** O título da tabela com os mesmos números (o gráfico enfeita, a tabela garante). */
  rotuloDaTabela: string;
  /** O `data-grafico` da raiz, para a prova achar a peça. */
  dado: string;
  altura?: number;
  /**
   * OPCIONAL (29/09): a cor da série quando ela é a ÚNICA do gráfico — a linha,
   * os marcadores (cheios e vazados) e a chave dela na dica. É a marca de série
   * única da casa, o `acento`: a média geral da Visão geral saía na cor da
   * série 1, que é também a dos promotores do NPS dois blocos abaixo, e ao lado
   * das colunas do volume, que já usam o `acento` — dois azuis no mesmo par de
   * gráficos, e o mesmo azul para duas coisas. Com duas séries ou mais ela é
   * ignorada: aí a cor é da POSIÇÃO, e uma cor só para todas as juntaria.
   */
  corDaSerieUnica?: string;
  /**
   * OPCIONAL (000334): até quando o hub sabe (o horizonte do período), para a
   * dica, a tabela e a nota dizerem "parcial, até às 16:50" no balde que o
   * contém. O ESTADO de cada balde vem no próprio balde.
   */
  horizonte?: string | null;
};

// ── As peças comuns aos dois gráficos do tempo ──────────────────────────────

/**
 * As palavras do balde parcial e dos que ainda não chegaram, para a nota do
 * horizonte (000334). "Em andamento" saiu na revisão de 02/10: o balde parcial é
 * o do horizonte, e o horizonte pode ser de dias atrás (a importação roda à mão).
 */
const PALAVRAS_DO_HORIZONTE: Record<Granularidade, { desse: string; um: string; varios: string }> = {
  hora: { desse: "dessa hora", um: "hora", varios: "horas" },
  dia: { desse: "desse dia", um: "dia", varios: "dias" },
  semana: { desse: "dessa semana", um: "semana", varios: "semanas" },
  mes: { desse: "desse mês", um: "mês", varios: "meses" },
};

/**
 * A nota do HORIZONTE (000334), debaixo do gráfico, com o mesmo desenho que ele
 * usa: o balde que contém o horizonte está incompleto no hub (a linha chega a ele
 * PONTILHADA; a coluna dele é LISTRADA), e os que começam depois dele ficam em
 * branco — em branco, e não em zero, porque o hub só não sabe ainda. É o que
 * impede a última coluna, pela metade, de se ler como queda. A data do horizonte
 * vai na nota sempre, menos por hora: ela não está ao lado do balde.
 */
function NotaDoHorizonte({
  g,
  forma,
  horizonte,
  temParcial,
  quantosAChegar,
}: {
  g: Granularidade;
  forma: "linha" | "coluna";
  horizonte: string | null | undefined;
  temParcial: boolean;
  quantosAChegar: number;
}) {
  const temAChegar = quantosAChegar > 0;
  if (!temParcial && !temAChegar) return null;
  const p = PALAVRAS_DO_HORIZONTE[g];
  const q = horizonte ? quandoLegivel(horizonte) : null;
  const ate = horizonte ? ateOHorizonte(horizonte, g, g !== "hora") : null;
  // "Depois das 16:50" por hora; "depois de 30/09, às 16:50" nos outros.
  const depois = q ? (g === "hora" ? `Depois ${contracaoDaHora(q.hora)} ${q.hora}` : `Depois de ${q.dia.slice(0, 5)}, ${horaComArtigo(q.hora)}`) : null;
  const quantos = quantosAChegar === 1 ? `${g === "hora" || g === "semana" ? "a" : "o"} ${p.um} ainda não chegou` : `${g === "hora" || g === "semana" ? "as" : "os"} ${numero(quantosAChegar)} ${p.varios} ainda não chegaram`;
  return (
    <p className="mt-2 flex items-start gap-2 text-xs leading-snug text-cinza" data-nota-do-horizonte>
      {temParcial && (
        <svg aria-hidden="true" width="16" height="12" viewBox="0 0 16 12" className="mt-0.5 shrink-0">
          {forma === "linha" ? (
            <line x1="2" y1="6" x2="15" y2="6" style={{ stroke: "var(--cf-cinza)", strokeWidth: 2, strokeDasharray: "0.1 4", strokeLinecap: "round" }} />
          ) : (
            <>
              <rect x="3" y="1" width="10" height="10" rx="1.5" style={{ fill: "var(--cf-superficie)", stroke: "var(--cf-acento)", strokeWidth: 1.5 }} />
              <path d="M4 8 8 4M6 10 12 4M9 10 12 7" style={{ stroke: "var(--cf-acento)", strokeWidth: 1.5 }} />
            </>
          )}
        </svg>
      )}
      <span>
        {temParcial &&
          `${forma === "linha" ? "Pontilhado" : "Coluna listrada"}: o hub tem as avaliações ${p.desse} só ${ate ?? "em parte"}.`}
        {temParcial && temAChegar && " "}
        {temAChegar &&
          `${depois ? `${depois}, ${quantos}` : quantos.charAt(0).toUpperCase() + quantos.slice(1)} ao hub: ${quantosAChegar === 1 ? "fica" : "ficam"} em branco, e não com zero.`}
      </span>
    </p>
  );
}

/** A altura padrão das duas: na Visão geral a coluna e a linha ficam lado a lado, e alturas iguais alinham os dois eixos. */
const ALTURA = 280;

/** O balde que a dica mostra, e se quem o escolheu foi o teclado — só então o leitor de tela é avisado. */
type Foco = { i: number; teclado: boolean };

/** As palavras de cada granularidade, para o leitor de tela, a tabela e a nota do ponto vazado. */
const PALAVRAS_DO_BALDE: Record<Granularidade, { coluna: string; percorre: string; naquele: string }> = {
  hora: { coluna: "Hora", percorre: "as horas", naquele: "naquela hora" },
  dia: { coluna: "Dia", percorre: "os dias", naquele: "naquele dia" },
  semana: { coluna: "Semana", percorre: "as semanas", naquele: "naquela semana" },
  mes: { coluna: "Mês", percorre: "os meses", naquele: "naquele mês" },
};

/**
 * O nome da RAIZ focável: a instrução do teclado. A descrição do desenho mora
 * no `<svg role="img">`, e repeti-la aqui faria o leitor de tela lê-la duas
 * vezes seguidas.
 */
function instrucaoDoTeclado(g: Granularidade): string {
  return `Gráfico: as setas percorrem ${PALAVRAS_DO_BALDE[g].percorre}, Home e End vão ao primeiro e ao último, e Esc esconde a dica. Os números estão também na tabela logo abaixo do gráfico.`;
}

function avaliacoesEmTexto(n: number): string {
  return `${numero(n)} ${n === 1 ? "avaliação" : "avaliações"}`;
}

/** Só número de verdade vira ponto: um `undefined` de série desalinhada não pode virar zero. */
function numeroOuNulo(v: number | null | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * A largura em que o gráfico é desenhado: a do espaço, medida. O elemento
 * medido existe SEMPRE (mesmo sem pontos): o efeito roda uma vez, na montagem,
 * e um elemento que só aparecesse depois ficaria com largura zero para sempre.
 * `useLayoutEffect` mede antes da pintura, então ninguém vê o quadro vazio.
 */
function useLargura<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [largura, setLargura] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setLargura(Math.round(el.getBoundingClientRect().width));
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, largura] as const;
}

/**
 * Quantos pixels um rótulo do eixo do tempo ocupa, com a folga até o vizinho,
 * no Inter a 11px: "00h" tem uns 19px, "29/09" uns 29 e "set/26" uns 32. É o
 * que decide de quantos em quantos baldes o eixo escreve — com 30 dias numa
 * coluna de 420px, um rótulo por dia encostaria um no outro.
 */
const LARGURA_DO_ROTULO: Record<Granularidade, number> = { hora: 34, dia: 44, semana: 44, mes: 50 };
/** A hora anda por divisores de 24, para o eixo ler 00h, 03h, 06h… e não 00h, 05h, 10h. */
const PASSOS_DA_HORA = [1, 2, 3, 4, 6, 8, 12, 24];

function passoDosRotulos(espacamento: number, g: Granularidade): number {
  const k = Math.max(1, Math.ceil(LARGURA_DO_ROTULO[g] / Math.max(espacamento, 0.01)));
  return g === "hora" ? (PASSOS_DA_HORA.find((p) => p >= k) ?? 24) : k;
}

/**
 * Se o balde `i` leva rótulo. A hora conta do ZERO (00h, 03h, 06h…). O resto
 * conta do FIM: o balde mais recente — hoje, esta semana — tem sempre o rótulo,
 * e o passo fica igual até o fim. Contar do começo e "forçar o último" (a forma
 * da 000327) emendava dois rótulos no fim sempre que o total não era múltiplo
 * do passo: com 30 dias e passo 4, "27/09" e "28/09" colados.
 */
function temRotulo(i: number, n: number, k: number, g: Granularidade): boolean {
  return g === "hora" ? i % k === 0 : (n - 1 - i) % k === 0;
}

/**
 * A dica mora ao lado da mira: à direita, e à esquerda quando não cabe — sem
 * nunca sair do quadro do gráfico, que no celular tem 300px.
 */
function esquerdaDaDica(x: number, larguraDaDica: number, W: number): number {
  if (x + 14 + larguraDaDica <= W) return x + 14;
  return Math.max(0, Math.min(W - larguraDaDica, x - 14 - larguraDaDica));
}

/**
 * As três vias do teclado e do ponteiro, iguais nos dois gráficos do tempo:
 * as setas, Home e End andam pelos baldes; Esc esconde a dica (o conteúdo que
 * aparece no foco tem de poder sumir sem que a pessoa tire o foco dali); e o
 * foco que chega por TAB mostra o último balde — o mais recente — e avisa o
 * leitor de tela. Os três conferem que o evento é da RAIZ: a tabela do
 * `<details>` mora dentro dela, e a seta no `<summary>` não pode andar no
 * gráfico.
 */
function useNavegacao(n: number, ativo: boolean) {
  const raiz = useRef<HTMLDivElement>(null);
  const [foco, setFoco] = useState<Foco | null>(null);
  const focoAtual = foco !== null && foco.i < n ? foco : null;

  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget || !ativo || n === 0) return;
    if (e.key === "Escape") {
      if (focoAtual) {
        e.preventDefault();
        setFoco(null);
      }
      return;
    }
    const atual = focoAtual?.i ?? n - 1;
    const proximo =
      e.key === "ArrowLeft"
        ? atual - 1
        : e.key === "ArrowRight"
          ? atual + 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? n - 1
              : null;
    if (proximo === null) return;
    e.preventDefault();
    setFoco({ i: Math.min(n - 1, Math.max(0, proximo)), teclado: true });
  }

  function aoFocar(e: FocusEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget || !ativo || n === 0) return;
    // O clique também foca a raiz; só o foco VISÍVEL (o do teclado) é anunciado.
    const teclado = e.currentTarget.matches(":focus-visible");
    setFoco((f) => f ?? { i: n - 1, teclado });
  }

  function aoDesfocar(e: FocusEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget) return;
    setFoco(null);
  }

  /** O ponteiro escolhe o balde; o mesmo balde de novo não desenha a tela outra vez. */
  function apontar(i: number) {
    setFoco((f) => (f && f.i === i && !f.teclado ? f : { i, teclado: false }));
  }

  /**
   * Só o MOUSE esconde a dica ao sair. No toque, `pointerleave` chega logo depois
   * do `pointerup`, e a dica sumiria no instante em que o dedo a pediu; ela fica
   * até o próximo toque ou até o foco sair.
   */
  function sair(e: PointerEvent<Element>) {
    if (e.pointerType === "mouse" && document.activeElement !== raiz.current) setFoco(null);
  }

  return { raiz, focoAtual, aoTeclar, aoFocar, aoDesfocar, apontar, sair };
}

/**
 * O que o leitor de tela ouve quando o TECLADO muda de balde. Uma região viva
 * que existe desde a montagem — a dica visual nasce e morre a cada balde, e uma
 * região viva que nasce junto com o texto costuma ser anunciada tarde ou nunca.
 */
function Anuncio({ texto }: { texto: string }) {
  return (
    <p className="sr-only" aria-live="polite" data-anuncio-do-grafico>
      {texto}
    </p>
  );
}

/** A nota do ponto vazado, com a mesma forma que o gráfico desenha. */
function NotaDasPoucas({ g }: { g: Granularidade }) {
  return (
    <p className="mt-2 flex items-center gap-2 text-xs text-cinza" data-nota-das-poucas>
      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" className="shrink-0">
        <circle cx="6" cy="6" r="4" style={{ fill: "var(--cf-superficie)", stroke: "var(--cf-cinza)", strokeWidth: 2 }} />
      </svg>
      Ponto vazado: menos de {POUCAS} avaliações {PALAVRAS_DO_BALDE[g].naquele} — uma média assim ainda muda com uma
      pessoa só.
    </p>
  );
}

// ── O eixo das notas ────────────────────────────────────────────────────────

/**
 * O eixo das NOTAS, com zoom: de um inteiro abaixo do menor valor (ou da menor
 * meta), com 0,3 de folga, até 10 — a regra do `Grafico` da 000327. Nunca menos
 * de dois pontos de altura: com um só, a oscilação de 0,05 ocuparia o gráfico
 * inteiro e pareceria um tombo. Os números do eixo estão sempre à vista, e são
 * eles que dizem que o eixo não começa no zero.
 */
function eixoDasNotas(valores: number[]): { lo: number; hi: number; ticks: number[] } {
  const hi = 10;
  let lo = valores.length ? Math.max(0, Math.floor(Math.min(...valores) - 0.3)) : 0;
  if (hi - lo < 2) lo = Math.max(0, hi - 2);
  const passo = hi - lo <= 5 ? 1 : 2;
  // Com passo 2, o chão tem de ser PAR: senão a linha de grade de baixo fica
  // sem número (de 3 a 10 as marcas seriam 10, 8, 6 e 4, e o 3 sumiria).
  if (passo === 2 && (hi - lo) % 2 === 1) lo -= 1;
  const ticks: number[] = [];
  for (let t = hi; t >= lo; t -= passo) ticks.push(t);
  return { lo, hi, ticks };
}

/**
 * O MARCADOR VAZADO: a mesma forma do `Marcador` (a série continua sendo
 * reconhecida pela forma), com a superfície por dentro e a cor na borda.
 *
 * A geometria é a do `Marcador` de `AdminAvaliacoesUi.tsx`, escrita de novo
 * porque ele só sabe desenhar cheio e aquele arquivo é do contrato: é a
 * segunda definição da mesma forma, e quem mudar uma forma lá tem de mudar
 * aqui também. O conserto é o `Marcador` ganhar um `vazado` — e esta cópia sai.
 * `cor`, como lá, troca só a cor.
 */
function MarcadorVazado({ p, x, y, tamanho = 9, cor }: { p: Posicao; x: number; y: number; tamanho?: number; cor?: string }) {
  const r = tamanho / 2;
  const estilo = { fill: "var(--cf-superficie)", stroke: cor ?? corDaSerie(p), strokeWidth: 2 } as const;
  if (p === 1) return <circle cx={x} cy={y} r={r} style={estilo} />;
  if (p === 2) return <rect x={x - r * 0.88} y={y - r * 0.88} width={r * 1.76} height={r * 1.76} style={estilo} />;
  if (p === 3) {
    const d = r * 1.18;
    return <path d={`M${x} ${y - d}L${x + d} ${y}L${x} ${y + d}L${x - d} ${y}Z`} style={estilo} />;
  }
  const h = r * 1.15;
  return <path d={`M${x} ${y - h}L${x + h} ${y + h * 0.8}L${x - h} ${y + h * 0.8}Z`} style={{ ...estilo, strokeLinejoin: "round" }} />;
}

// ── O gráfico de linhas ─────────────────────────────────────────────────────

type PontoDaLinha = { i: number; x: number; y: number; valor: number; avaliacoes: number };

/**
 * A evolução das NOTAS no tempo (0 a 10): a média geral da Visão geral, as
 * quatro notas de uma ferramenta e as até quatro ferramentas do Comparar.
 *
 * Para contagem, o gráfico é o de colunas: número de avaliações é COMPRIMENTO,
 * e o eixo com zoom daqui mentiria sobre ele.
 */
export function GraficoDeLinhas({
  baldes,
  granularidade: g,
  series,
  metas = [],
  descricao,
  semPontos,
  rotuloDaTabela,
  dado,
  altura = ALTURA,
  corDaSerieUnica,
  horizonte,
}: PropsDoGraficoDeLinhas) {
  const n = baldes.length;
  const valor = (s: SerieDoGrafico, i: number) => numeroOuNulo(s.valores[i]);
  const contagem = (s: SerieDoGrafico, i: number) => s.contagens[i] ?? 0;
  // O estado de cada balde contra o horizonte vem no próprio balde (000334).
  // O que ainda não chegou tem valor NULO, e a linha já quebra nele sozinha.
  const estadoDe = (i: number) => baldes[i]?.estado ?? "completo";
  // A cor que a série PINTA: a da posição, ou a da série única quando quem chama
  // a deu e a série é mesmo a única (ver `corDaSerieUnica`).
  const corDe = (s: SerieDoGrafico) => (corDaSerieUnica && series.length === 1 ? corDaSerieUnica : corDaSerie(s.p));
  const temPontos = n > 0 && series.some((s) => baldes.some((_, i) => valor(s, i) !== null));
  const quantosAChegar = baldes.filter((b) => b.estado === "a_chegar").length;

  const [area, largura] = useLargura<HTMLDivElement>();
  const nav = useNavegacao(n, temPontos);
  const foco = nav.focoAtual;

  // A mesma meta duas vezes (duas ferramentas com 9,0 no Comparar) é UMA linha:
  // duas tracejadas uma sobre a outra só engrossariam o traço.
  const metasUnicas = metas
    .filter((m, k) => Number.isFinite(m.valor) && metas.findIndex((o) => o.valor === m.valor) === k)
    .sort((a, b) => b.valor - a.valor);

  const valoresDoEixo = [
    ...series.flatMap((s) => baldes.map((_, i) => valor(s, i)).filter((v): v is number => v !== null)),
    ...metasUnicas.map((m) => m.valor),
  ];
  const { lo, hi, ticks } = eixoDasNotas(valoresDoEixo);

  // A contagem é a MESMA em todas as séries quando elas são notas das mesmas
  // avaliações (as quatro de uma ferramenta, ou a média geral sozinha). Aí ela
  // é dita uma vez por balde, no alto da dica e numa coluna da tabela — quatro
  // "· 12 avaliações" iguais na mesma dica são ruído.
  const contagemUnica = series.length > 0 && series.every((s) => baldes.every((_, i) => contagem(s, i) === contagem(series[0], i)));

  // As margens: à esquerda os números do eixo ("10"); à direita o rótulo da
  // meta e o do fim da linha. A margem da meta cresce com o rótulo que quem
  // chama escreveu, para ele nunca sair do quadro.
  const maiorRotuloDaMeta = Math.max(0, ...metasUnicas.map((m) => m.rotulo.length));
  const M = {
    esq: 34,
    dir: metasUnicas.length ? Math.min(Math.max(46, 12 + Math.ceil(maiorRotuloDaMeta * 6.2)), Math.max(46, largura * 0.35)) : 46,
    topo: 14,
    base: 28,
  };
  const W = largura;
  const plotW = Math.max(1, W - M.esq - M.dir);
  const plotH = Math.max(1, altura - M.topo - M.base);
  const espacamento = n > 1 ? plotW / (n - 1) : plotW;
  const x = (i: number) => M.esq + (n > 1 ? i * espacamento : plotW / 2);
  const y = (v: number) => M.topo + ((hi - v) / (hi - lo)) * plotH;

  // Os segmentos de cada série: um balde SEM avaliação quebra a linha.
  const desenho = series.map((s) => {
    const segmentos: PontoDaLinha[][] = [];
    let atual: PontoDaLinha[] = [];
    for (let i = 0; i < n; i++) {
      const v = valor(s, i);
      if (v === null) {
        if (atual.length) segmentos.push(atual);
        atual = [];
        continue;
      }
      atual.push({ i, x: x(i), y: y(v), valor: v, avaliacoes: contagem(s, i) });
    }
    if (atual.length) segmentos.push(atual);
    const ultimo = segmentos.at(-1)?.at(-1) ?? null;
    return { s, segmentos, ultimo };
  });
  // A nota fala do pontilhado só quando ele foi DESENHADO: o parcial com o balde
  // anterior na mesma série. Um ponto parcial isolado (a hora de antes vazia)
  // não tem trecho, e a nota descreveria o que não está lá (a revisão de 02/10).
  const temParcial = desenho.some(({ segmentos }) => segmentos.some((seg) => seg.findIndex((pt) => estadoDe(pt.i) === "parcial") > 0));

  // Os marcadores. Com espaço, um em cada ponto. Num eixo apertado (30 dias no
  // celular dão 7px entre pontos, e o marcador tem 9), marcadores encostados
  // viram uma faixa grossa que esconde a própria linha — então ficam só os que
  // dizem o que a linha não diz: o ponto ISOLADO (entre dois baldes vazios ele
  // não tem linha, e sem marcador ele não existiria na tela), o ponto de POUCAS
  // avaliações (o aviso) e o último de cada série. A dica e a tabela continuam
  // com todos.
  const cabemTodos = espacamento >= 12;
  const desenhaMarcador = (seg: PontoDaLinha[], pt: PontoDaLinha, ultimo: PontoDaLinha | null) =>
    cabemTodos || seg.length === 1 || pt.avaliacoes < POUCAS || pt === ultimo;
  const temPoucas = desenho.some(({ segmentos, ultimo }) =>
    segmentos.some((seg) => seg.some((pt) => pt.avaliacoes < POUCAS && desenhaMarcador(seg, pt, ultimo))),
  );

  // O rótulo da META mora na margem direita, e só se não encosta no de outra
  // meta: duas metas de 9,0 e 9,1 ficam a menos de 13px uma da outra, e dois
  // rótulos sobrepostos não se leem. A linha tracejada fica, e o valor de cada
  // meta está na leitura de quem chama (a tabela "Lado a lado", a ficha).
  const rotulosDaMeta: { valor: number; rotulo: string; y: number }[] = [];
  for (const m of metasUnicas) {
    const ym = y(m.valor);
    if (rotulosDaMeta.every((r) => Math.abs(r.y - ym) >= 13)) rotulosDaMeta.push({ ...m, y: ym });
  }

  // O rótulo no fim de cada linha — só se NENHUM encosta noutro nem num da
  // meta. Empurrar rótulos para descolar os separa das linhas deles, e aí a
  // legenda e a dica dizem melhor.
  const rotulosDoFim = desenho
    .filter((d) => d.ultimo !== null)
    .map((d) => ({ id: d.s.id, p: d.s.p, x: d.ultimo!.x, y: d.ultimo!.y, texto: notaLegivel(d.ultimo!.valor) }));
  const colidem =
    rotulosDoFim.some((a, k) => rotulosDoFim.some((b, j) => j > k && Math.abs(a.y - b.y) < 14 && Math.abs(a.x - b.x) < 44)) ||
    rotulosDoFim.some((a) => rotulosDaMeta.some((b) => Math.abs(a.y - b.y) < 14 && Math.abs(a.x - (M.esq + plotW)) < 44));

  const passo = passoDosRotulos(espacamento, g);

  function indiceDoPonteiro(e: PointerEvent<SVGRectElement>): number | null {
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg || n === 0) return null;
    const px = e.clientX - svg.getBoundingClientRect().left;
    const i = n <= 1 ? 0 : Math.round(((px - M.esq) / plotW) * (n - 1));
    return Math.min(n - 1, Math.max(0, i));
  }
  function aoApontar(e: PointerEvent<SVGRectElement>) {
    const i = indiceDoPonteiro(e);
    if (i !== null) nav.apontar(i);
  }

  const larguraDaDica = Math.min(248, W);
  const xFoco = foco ? x(foco.i) : 0;
  const baldeEmFoco = foco ? baldes[foco.i] : null;

  const estadoDoFoco = baldeEmFoco ? rotuloDoEstadoDoBalde(baldeEmFoco, g, horizonte) : null;
  const aChegarNoFoco = foco ? estadoDe(foco.i) === "a_chegar" : false;
  const anuncio =
    foco && foco.teclado && baldeEmFoco
      ? `${rotuloLongoDoBalde(baldeEmFoco, g)}${estadoDoFoco ? `, ${estadoDoFoco}` : ""}${
          contagemUnica && !aChegarNoFoco ? `, ${avaliacoesEmTexto(contagem(series[0], foco.i))}` : ""
        }: ${series
          .map((s) => {
            const v = valor(s, foco.i);
            const c = contagem(s, foco.i);
            return `${s.rotulo}, ${v === null ? (aChegarNoFoco ? "ainda não chegou" : "sem avaliação") : notaLegivel(v)}${
              !contagemUnica && v !== null ? `, ${avaliacoesEmTexto(c)}` : ""
            }`;
          })
          .join("; ")}.`
      : "";

  return (
    <div
      ref={nav.raiz}
      className="relative rounded-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-acento"
      tabIndex={temPontos ? 0 : undefined}
      role="group"
      aria-label={temPontos ? instrucaoDoTeclado(g) : descricao}
      onKeyDown={nav.aoTeclar}
      onFocus={nav.aoFocar}
      onBlur={nav.aoDesfocar}
      data-grafico={dado}
      data-baldes={n}
      data-granularidade={g}
      data-foco={baldeEmFoco?.chave}
    >
      {/* A legenda existe a partir de DUAS séries: com uma só, o título do bloco
          já diz o que está desenhado, e uma caixa com uma cor repetiria o título. */}
      {series.length >= 2 && (
        <ul className="mb-3 flex flex-wrap gap-x-5 gap-y-1.5" data-legenda-do-grafico>
          {series.map((s) => (
            <li key={s.id} className="flex items-center gap-2 text-sm text-tinta" data-legenda={s.id} data-posicao={s.p}>
              <ChaveDaSerie p={s.p} />
              <span className="font-semibold">{s.rotulo}</span>
              {!baldes.some((_, i) => valor(s, i) !== null) && <span className="text-cinza">sem avaliação no período</span>}
            </li>
          ))}
        </ul>
      )}

      <div ref={area} className="relative">
        {!temPontos ? (
          <p className="py-10 text-center text-sm text-cinza" data-grafico-sem-pontos>
            {semPontos}
          </p>
        ) : W === 0 ? (
          <div style={{ height: altura }} />
        ) : (
          <svg
            width={W}
            height={altura}
            role="img"
            aria-label={descricao}
            className="block overflow-visible"
            data-marcadores={cabemTodos ? "todos" : "essenciais"}
          >
            {/* As linhas de grade: fio de 1px, contínuo, um passo fora da superfície. */}
            {ticks.map((t) => (
              <g key={t} data-marca-do-eixo={t}>
                <line x1={M.esq} x2={M.esq + plotW} y1={y(t)} y2={y(t)} style={{ stroke: "var(--cf-borda)", strokeWidth: 1 }} />
                <text x={M.esq - 8} y={y(t) + 4} textAnchor="end" fontSize="11" style={{ fill: "var(--cf-cinza)" }} className="tabular-nums">
                  {t}
                </text>
              </g>
            ))}
            {baldes.map((b, i) =>
              temRotulo(i, n, passo, g) ? (
                <text
                  key={b.chave}
                  x={x(i)}
                  y={altura - 8}
                  textAnchor="middle"
                  fontSize="11"
                  style={{ fill: "var(--cf-cinza)" }}
                  className="tabular-nums"
                  data-rotulo-do-eixo={b.chave}
                >
                  {rotuloCurtoDoBalde(b, g)}
                </text>
              ) : null,
            )}
            {/* A meta: tracejada, porque não é grade — é a régua que o time escolheu.
                O rótulo vai no FIM da linha, na margem: no começo ele caía em cima
                do primeiro ponto de uma série que começou perto da meta. */}
            {metasUnicas.map((m) => {
              const rotulo = rotulosDaMeta.find((r) => r.valor === m.valor);
              return (
                <g key={m.valor} data-linha-da-meta={m.valor}>
                  <line
                    x1={M.esq}
                    x2={M.esq + plotW}
                    y1={y(m.valor)}
                    y2={y(m.valor)}
                    style={{ stroke: "var(--cf-cinza)", strokeWidth: 1, strokeDasharray: "5 4" }}
                  />
                  {rotulo && (
                    <text x={M.esq + plotW + 6} y={y(m.valor) + 4} fontSize="11" style={{ fill: "var(--cf-cinza)" }} data-rotulo-da-meta>
                      {m.rotulo}
                    </text>
                  )}
                </g>
              );
            })}
            {desenho.map(({ s, segmentos, ultimo }) => (
              <g key={s.id} data-serie={s.id} data-posicao={s.p}>
                {segmentos.map((seg, k) => {
                  // O trecho que CHEGA ao balde parcial é PONTILHADO (000334): o
                  // hub ainda não tem o balde inteiro, e a linha cheia até ele o
                  // leria como fechado. Não é o vazado — o vazado já quer dizer
                  // "poucas avaliações" —, nem o tracejado, que é a meta (a
                  // revisão de 02/10 achou os dois tracejados no mesmo gráfico).
                  const iParcial = seg.findIndex((pt) => estadoDe(pt.i) === "parcial");
                  const pedacos =
                    iParcial > 0
                      ? [
                          { pts: seg.slice(0, iParcial), pontilhado: false },
                          { pts: seg.slice(iParcial - 1, iParcial + 1), pontilhado: true },
                          { pts: seg.slice(iParcial), pontilhado: false },
                        ]
                      : [{ pts: seg, pontilhado: false }];
                  return pedacos.map((pd, j) =>
                    pd.pts.length > 1 ? (
                      <path
                        key={`${k}-${j}`}
                        d={pd.pts.map((pt, m) => `${m ? "L" : "M"}${pt.x} ${pt.y}`).join("")}
                        style={{
                          fill: "none",
                          stroke: corDe(s),
                          strokeWidth: 2,
                          strokeLinejoin: "round",
                          strokeLinecap: "round",
                          strokeDasharray: pd.pontilhado ? "0.1 5" : undefined,
                        }}
                        data-linha-da-serie
                        data-pontilhado={pd.pontilhado ? "parcial" : undefined}
                        data-de={pd.pontilhado ? baldes[pd.pts[0].i].chave : undefined}
                        data-ate={pd.pontilhado ? baldes[pd.pts[pd.pts.length - 1].i].chave : undefined}
                      />
                    ) : null,
                  );
                })}
                {segmentos.map((seg) =>
                  seg.map((pt) =>
                    desenhaMarcador(seg, pt, ultimo) ? (
                      <g
                        key={pt.i}
                        data-marcador={baldes[pt.i].chave}
                        data-poucas={pt.avaliacoes < POUCAS ? "true" : undefined}
                        data-estado={estadoDe(pt.i)}
                      >
                        {pt.avaliacoes < POUCAS ? (
                          <MarcadorVazado p={s.p} x={pt.x} y={pt.y} cor={corDe(s)} />
                        ) : (
                          <Marcador p={s.p} x={pt.x} y={pt.y} cor={corDe(s)} />
                        )}
                      </g>
                    ) : null,
                  ),
                )}
              </g>
            ))}
            {!colidem &&
              rotulosDoFim.map((r) => (
                <text
                  key={r.id}
                  x={r.x + 10}
                  y={r.y + 4}
                  fontSize="12"
                  fontWeight="600"
                  style={{ fill: "var(--cf-tinta)" }}
                  className="tabular-nums"
                  data-rotulo-do-fim={r.p}
                >
                  {r.texto}
                </text>
              ))}
            {foco && (
              <line
                x1={xFoco}
                x2={xFoco}
                y1={M.topo}
                y2={M.topo + plotH}
                style={{ stroke: "var(--cf-cinza)", strokeWidth: 1 }}
                data-mira
              />
            )}
            {/* A área que ouve o ponteiro é o gráfico INTEIRO: quem aponta mira
                um balde, nunca uma linha de 2px. O toque escolhe no `pointerdown`,
                porque o dedo parado não gera movimento nenhum. */}
            <rect
              x={M.esq - 12}
              y={M.topo}
              width={plotW + 24}
              height={plotH}
              fill="transparent"
              onPointerMove={aoApontar}
              onPointerDown={aoApontar}
              onPointerLeave={nav.sair}
              data-area-do-ponteiro
            />
          </svg>
        )}

        {foco && baldeEmFoco && temPontos && W > 0 && (
          <div
            className="pointer-events-none absolute z-10 rounded-xl border border-borda bg-superficie p-3 text-xs shadow-lg"
            style={{ left: esquerdaDaDica(xFoco, larguraDaDica, W), top: M.topo, width: larguraDaDica }}
            aria-hidden="true"
            data-dica-do-grafico={baldeEmFoco.chave}
          >
            <p className="font-semibold text-tinta">
              {rotuloLongoDoBalde(baldeEmFoco, g)}
              {estadoDoFoco && (
                <span className="font-normal text-cinza" data-dica-estado>
                  {" · "}
                  {estadoDoFoco}
                </span>
              )}
              {contagemUnica && !aChegarNoFoco && (
                <span className="font-normal text-cinza">
                  {" · "}
                  {avaliacoesEmTexto(contagem(series[0], foco.i))}
                  {contagem(series[0], foco.i) > 0 && contagem(series[0], foco.i) < POUCAS && " (poucas)"}
                </span>
              )}
            </p>
            <ul className="mt-2 space-y-1.5">
              {series.map((s) => {
                const v = valor(s, foco.i);
                const c = contagem(s, foco.i);
                return (
                  <li key={s.id} className="flex items-center gap-2" data-dica-serie={s.id}>
                    <ChaveDaSerie p={s.p} cor={corDe(s)} />
                    {/* O valor na frente e forte, o nome depois: na dica quem lê já
                        sabe a série e quer o número. */}
                    <span className="font-display text-sm font-bold tabular-nums text-titulo">{v === null ? "—" : notaLegivel(v)}</span>
                    <span className="min-w-0 truncate text-cinza" title={s.rotulo}>
                      {s.rotulo}
                      {!contagemUnica &&
                        !aChegarNoFoco &&
                        (v === null ? " · sem avaliação" : ` · ${avaliacoesEmTexto(c)}${c < POUCAS ? " (poucas)" : ""}`)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {temPontos && temPoucas && <NotaDasPoucas g={g} />}
      {temPontos && <NotaDoHorizonte g={g} forma="linha" horizonte={horizonte} temParcial={temParcial} quantosAChegar={quantosAChegar} />}
      <Anuncio texto={anuncio} />

      {temPontos && (
        <TabelaDasLinhas
          baldes={baldes}
          g={g}
          series={series}
          contagemUnica={contagemUnica}
          rotulo={rotuloDaTabela}
          horizonte={horizonte}
        />
      )}
    </div>
  );
}

/** Os mesmos números, em tabela: um balde por linha, inclusive os vazios — o eixo é o TEMPO. */
function TabelaDasLinhas({
  baldes,
  g,
  series,
  contagemUnica,
  rotulo,
  horizonte,
}: {
  baldes: Balde[];
  g: Granularidade;
  series: SerieDoGrafico[];
  contagemUnica: boolean;
  rotulo: string;
  horizonte?: string | null;
}) {
  return (
    <details className="mt-4" data-tabela-do-grafico>
      <summary className="cursor-pointer text-sm font-semibold text-link">{rotulo}</summary>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[24rem] text-sm">
          <thead>
            <tr className="border-b border-borda text-left text-xs text-cinza">
              <th scope="col" className="py-1.5 pr-4 font-semibold">
                {PALAVRAS_DO_BALDE[g].coluna}
              </th>
              {series.map((s) => (
                <th key={s.id} scope="col" className="py-1.5 pr-4 text-right font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    {series.length >= 2 && <ChaveDaSerie p={s.p} />}
                    {s.rotulo}
                  </span>
                </th>
              ))}
              {contagemUnica && (
                <th scope="col" className="py-1.5 pr-4 text-right font-semibold">
                  Avaliações
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {baldes.map((b, i) => (
              <tr key={b.chave} className="border-b border-borda/60 last:border-0" data-balde={b.chave} data-estado={b.estado ?? "completo"}>
                <th scope="row" className="py-1.5 pr-4 text-left font-normal text-tinta">
                  {rotuloLongoDoBalde(b, g)}
                  {rotuloDoEstadoDoBalde(b, g, horizonte) && (
                    <span className="text-cinza"> ({rotuloDoEstadoDoBalde(b, g, horizonte)})</span>
                  )}
                </th>
                {series.map((s) => {
                  const v = numeroOuNulo(s.valores[i]);
                  const c = s.contagens[i] ?? 0;
                  return (
                    <td key={s.id} className="py-1.5 pr-4 text-right tabular-nums text-tinta" data-celula={`${s.id}|${b.chave}`}>
                      {v === null ? (
                        "—"
                      ) : (
                        <>
                          <strong className="font-semibold text-titulo">{notaLegivel(v)}</strong>
                          {!contagemUnica && <span className="text-cinza"> ({numero(c)})</span>}
                        </>
                      )}
                    </td>
                  );
                })}
                {contagemUnica && (
                  <td className="py-1.5 pr-4 text-right tabular-nums text-tinta" data-celula={`avaliacoes|${b.chave}`}>
                    {b.estado === "a_chegar" ? "—" : numero(series[0]?.contagens[i] ?? 0)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!contagemUnica && <p className="mt-2 text-xs text-cinza">Entre parênteses, quantas avaliações de alunos sustentam cada média.</p>}
      </div>
    </details>
  );
}

// ── O gráfico de colunas ────────────────────────────────────────────────────

export type PropsDoGraficoDeColunas = {
  baldes: Balde[];
  granularidade: Granularidade;
  /** Alinhado a `baldes` (nulo no balde que ainda não chegou). */
  valores: (number | null)[];
  descricao: string;
  /** Como a dica e a tabela escrevem um valor: "12 avaliações". */
  rotuloDoValor: (n: number) => string;
  rotuloDaTabela: string;
  dado: string;
  altura?: number;
  /** OPCIONAL (000328): o texto quando todos os baldes têm zero. */
  semValores?: string;
  /** OPCIONAL (000328): o cabeçalho da coluna dos valores na tabela. */
  rotuloDaColuna?: string;
  /** OPCIONAL (000334): até quando o hub sabe, para dizer "parcial, até às 16:50" na coluna que o contém. */
  horizonte?: string | null;
};

/**
 * O eixo das CONTAGENS: do zero, em inteiros, com passos redondos (1, 2, 5,
 * 10, 20, 50…) e no máximo cinco marcas acima do zero. Coluna é comprimento, e
 * comprimento só se compara a partir do zero.
 */
function eixoDeInteiros(maximo: number): { topo: number; ticks: number[] } {
  let passo = 1;
  if (maximo > 5) {
    const bruto = maximo / 4;
    const potencia = 10 ** Math.floor(Math.log10(bruto));
    passo = [1, 2, 5, 10].map((m) => m * potencia).find((p) => p >= bruto) ?? 10 * potencia;
  }
  const topo = Math.max(passo, Math.ceil(maximo / passo) * passo);
  const ticks: number[] = [];
  for (let t = 0; t <= topo; t += passo) ticks.push(t);
  return { topo, ticks };
}

/**
 * Uma coluna com os cantos de CIMA arredondados (4px) e a base reta: a ponta
 * arredondada marca onde o dado acaba, e a base reta encosta na linha do zero.
 * O raio encolhe com a coluna — numa de 3px de altura, 4px de raio furaria a
 * base.
 */
function caminhoDaColuna(x0: number, largura: number, yTopo: number, yBase: number): string {
  const r = Math.max(0, Math.min(4, largura / 2, yBase - yTopo));
  const x1 = x0 + largura;
  return `M${x0} ${yBase}V${yTopo + r}A${r} ${r} 0 0 1 ${x0 + r} ${yTopo}H${x1 - r}A${r} ${r} 0 0 1 ${x1} ${yTopo + r}V${yBase}Z`;
}

/**
 * As CONTAGENS por balde (o volume de avaliações): colunas do zero, uma cor só
 * (`acento`, a marca de série única), no máximo 24px de largura e 2px de
 * superfície entre vizinhas — com 30 dias numa coluna de 420px cada uma tem
 * 12px, e sem a folga elas virariam um bloco.
 *
 * Cada balde é um `<g data-coluna>` com a área inteira da faixa dele como alvo
 * do ponteiro (maior que a coluna: a de um dia com 2 avaliações tem 8px de
 * altura, e ninguém acerta isso), e o balde de zero também existe — o zero é
 * dado.
 */
export function GraficoDeColunas({
  baldes,
  granularidade: g,
  valores,
  descricao,
  rotuloDoValor,
  rotuloDaTabela,
  dado,
  altura = ALTURA,
  semValores = "Nenhuma avaliação de aluno no período.",
  rotuloDaColuna = "Avaliações",
  horizonte,
}: PropsDoGraficoDeColunas) {
  const n = baldes.length;
  const valor = (i: number) => {
    const v = numeroOuNulo(valores[i]);
    return v !== null && v > 0 ? v : 0;
  };
  const maximo = Math.max(0, ...baldes.map((_, i) => valor(i)));
  const temValores = n > 0 && maximo > 0;
  // O estado de cada balde contra o horizonte vem no próprio balde (000334): o
  // que ainda não chegou não tem coluna — em branco, e não zero —, e o parcial
  // é LISTRADO, porque a coluna dele ainda está crescendo.
  const estadoDe = (i: number) => baldes[i]?.estado ?? "completo";
  // A nota só fala da listra quando há coluna listrada: a hora em andamento sem
  // avaliação nenhuma não desenha coluna, e a nota descreveria o que não está lá.
  const temParcial = baldes.some((b, i) => b.estado === "parcial" && valor(i) > 0);
  const quantosAChegar = baldes.filter((b) => b.estado === "a_chegar").length;
  const idDaListra = `listra-${useId().replace(/:/g, "")}`;
  const valorEmTexto = (i: number) => (estadoDe(i) === "a_chegar" ? "ainda não chegou" : rotuloDoValor(valor(i)));

  const [area, largura] = useLargura<HTMLDivElement>();
  const nav = useNavegacao(n, temValores);
  const foco = nav.focoAtual;

  const { topo: topoDoEixo, ticks } = eixoDeInteiros(maximo);
  // A margem esquerda cabe o maior número do eixo ("2.000" tem uns 32px); a
  // direita, a metade do rótulo "29/09" centrado na última coluna, quando ela é
  // estreita.
  const M = { esq: Math.max(26, 10 + Math.ceil(numero(topoDoEixo).length * 6.6)), dir: 12, topo: 18, base: 28 };
  const W = largura;
  const plotW = Math.max(1, W - M.esq - M.dir);
  const plotH = Math.max(1, altura - M.topo - M.base);
  const faixa = n > 0 ? plotW / n : plotW;
  const larguraDaColuna = Math.max(1, Math.min(24, faixa - 2));
  const centro = (i: number) => M.esq + faixa * (i + 0.5);
  const y = (v: number) => M.topo + plotH - (v / topoDoEixo) * plotH;
  const base = M.topo + plotH;

  // O rótulo direto vai numa coluna só: a MAIS ALTA (a mais recente, se houver
  // empate). Um número em cada uma das 30 é ruído que ninguém lê; o do pico diz
  // de que tamanho é o gráfico sem obrigar a subir até o eixo.
  let pico = -1;
  baldes.forEach((_, i) => {
    if (valor(i) > 0 && valor(i) >= maximo) pico = i;
  });

  const passo = passoDosRotulos(faixa, g);
  const larguraDaDica = Math.min(220, W);
  const baldeEmFoco = foco ? baldes[foco.i] : null;
  const estadoDoFoco = baldeEmFoco ? rotuloDoEstadoDoBalde(baldeEmFoco, g, horizonte) : null;
  const anuncio =
    foco && foco.teclado && baldeEmFoco
      ? `${rotuloLongoDoBalde(baldeEmFoco, g)}${
          estadoDoFoco && estadoDe(foco.i) !== "a_chegar" ? `, ${estadoDoFoco}` : ""
        }: ${valorEmTexto(foco.i)}.`
      : "";

  return (
    <div
      ref={nav.raiz}
      className="relative rounded-xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-acento"
      tabIndex={temValores ? 0 : undefined}
      role="group"
      aria-label={temValores ? instrucaoDoTeclado(g) : descricao}
      onKeyDown={nav.aoTeclar}
      onFocus={nav.aoFocar}
      onBlur={nav.aoDesfocar}
      data-grafico={dado}
      data-baldes={n}
      data-granularidade={g}
      data-foco={baldeEmFoco?.chave}
    >
      <div ref={area} className="relative">
        {!temValores ? (
          <p className="py-10 text-center text-sm text-cinza" data-grafico-sem-pontos>
            {semValores}
          </p>
        ) : W === 0 ? (
          <div style={{ height: altura }} />
        ) : (
          <svg
            width={W}
            height={altura}
            role="img"
            aria-label={descricao}
            className="block overflow-visible"
            onPointerLeave={nav.sair}
          >
            {/* A listra da coluna parcial: a cor do dado em faixas diagonais,
                com a superfície entre elas. Textura, e não cor mais clara: um tom
                mais claro do acento se leria como outra série, e a listra se lê
                sem cor nenhuma. */}
            {temParcial && (
              <defs>
                <pattern id={idDaListra} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="6" height="6" style={{ fill: "var(--cf-superficie)" }} />
                  <rect width="3" height="6" style={{ fill: "var(--cf-acento)" }} />
                </pattern>
              </defs>
            )}
            {/* A faixa do balde em foco, atrás de tudo: a coluna que a dica
                descreve "acende" sem mudar de cor — a cor é do dado. */}
            {foco && (
              <rect
                x={M.esq + faixa * foco.i}
                y={M.topo}
                width={faixa}
                height={plotH}
                style={{ fill: "var(--cf-chip)" }}
                data-faixa-em-foco
              />
            )}
            {ticks.map((t) => (
              <g key={t} data-marca-do-eixo={t}>
                <line x1={M.esq} x2={M.esq + plotW} y1={y(t)} y2={y(t)} style={{ stroke: "var(--cf-borda)", strokeWidth: 1 }} />
                <text x={M.esq - 8} y={y(t) + 4} textAnchor="end" fontSize="11" style={{ fill: "var(--cf-cinza)" }} className="tabular-nums">
                  {numero(t)}
                </text>
              </g>
            ))}
            {baldes.map((b, i) =>
              temRotulo(i, n, passo, g) ? (
                <text
                  key={b.chave}
                  x={centro(i)}
                  y={altura - 8}
                  textAnchor="middle"
                  fontSize="11"
                  style={{ fill: "var(--cf-cinza)" }}
                  className="tabular-nums"
                  data-rotulo-do-eixo={b.chave}
                >
                  {rotuloCurtoDoBalde(b, g)}
                </text>
              ) : null,
            )}
            {baldes.map((b, i) => {
              const v = valor(i);
              return (
                <g
                  key={b.chave}
                  data-coluna={b.chave}
                  data-valor={estadoDe(i) === "a_chegar" ? undefined : v}
                  data-estado={estadoDe(i)}
                  onPointerMove={() => nav.apontar(i)}
                  onPointerDown={() => nav.apontar(i)}
                >
                  <rect x={M.esq + faixa * i} y={M.topo} width={faixa} height={plotH} fill="transparent" />
                  {v > 0 && estadoDe(i) !== "a_chegar" && (
                    <path
                      d={caminhoDaColuna(centro(i) - larguraDaColuna / 2, larguraDaColuna, y(v), base)}
                      style={
                        estadoDe(i) === "parcial"
                          ? { fill: `url(#${idDaListra})`, stroke: "var(--cf-acento)", strokeWidth: 1.5 }
                          : { fill: "var(--cf-acento)" }
                      }
                    />
                  )}
                </g>
              );
            })}
            {pico >= 0 && (
              <text
                x={centro(pico)}
                y={y(valor(pico)) - 6}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                style={{ fill: "var(--cf-tinta)" }}
                className="pointer-events-none tabular-nums"
                data-rotulo-do-pico={baldes[pico].chave}
              >
                {numero(valor(pico))}
              </text>
            )}
          </svg>
        )}

        {foco && baldeEmFoco && temValores && W > 0 && (
          <div
            className="pointer-events-none absolute z-10 rounded-xl border border-borda bg-superficie p-3 text-xs shadow-lg"
            style={{ left: esquerdaDaDica(centro(foco.i), larguraDaDica, W), top: M.topo, width: larguraDaDica }}
            aria-hidden="true"
            data-dica-da-coluna={baldeEmFoco.chave}
          >
            <p className="font-semibold text-tinta">
              {rotuloLongoDoBalde(baldeEmFoco, g)}
              {estadoDoFoco && estadoDe(foco.i) !== "a_chegar" && (
                <span className="font-normal text-cinza" data-dica-estado>
                  {" · "}
                  {estadoDoFoco}
                </span>
              )}
            </p>
            <p className="mt-1 font-display text-sm font-bold tabular-nums text-titulo">{valorEmTexto(foco.i)}</p>
          </div>
        )}
      </div>

      {temValores && <NotaDoHorizonte g={g} forma="coluna" horizonte={horizonte} temParcial={temParcial} quantosAChegar={quantosAChegar} />}
      <Anuncio texto={anuncio} />

      {temValores && (
        <details className="mt-4" data-tabela-do-grafico>
          <summary className="cursor-pointer text-sm font-semibold text-link">{rotuloDaTabela}</summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[18rem] text-sm">
              <thead>
                <tr className="border-b border-borda text-left text-xs text-cinza">
                  <th scope="col" className="py-1.5 pr-4 font-semibold">
                    {PALAVRAS_DO_BALDE[g].coluna}
                  </th>
                  <th scope="col" className="py-1.5 pr-4 text-right font-semibold">
                    {rotuloDaColuna}
                  </th>
                </tr>
              </thead>
              <tbody>
                {baldes.map((b, i) => (
                  <tr key={b.chave} className="border-b border-borda/60 last:border-0" data-balde={b.chave} data-estado={estadoDe(i)}>
                    <th scope="row" className="py-1.5 pr-4 text-left font-normal text-tinta">
                      {rotuloLongoDoBalde(b, g)}
                      {estadoDe(i) === "parcial" && <span className="text-cinza"> ({rotuloDoEstadoDoBalde(b, g, horizonte)})</span>}
                    </th>
                    <td
                      className="py-1.5 pr-4 text-right tabular-nums text-tinta"
                      data-valor={estadoDe(i) === "a_chegar" ? undefined : valor(i)}
                    >
                      {valorEmTexto(i)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

// ── As barras horizontais ───────────────────────────────────────────────────

export type ItemDasBarras = {
  id: string;
  rotulo: string;
  valor: number;
  /** O texto no fim da barra: "235 · 24,1%". */
  texto: string;
  /** Quando existe, o rótulo é um link (a página da ferramenta). */
  href?: string;
};

export type PropsDasBarras = { itens: ItemDasBarras[]; descricao: string; dado: string };

/** O rótulo de uma linha: link quando há para onde ir, cortado com o nome inteiro no `title`. */
function RotuloDaLinha({ rotulo, href }: { rotulo: string; href?: string }) {
  return href ? (
    <Link to={href} className="block min-w-0 truncate text-sm font-semibold text-link hover:underline" title={rotulo}>
      {rotulo}
    </Link>
  ) : (
    <span className="block min-w-0 truncate text-sm text-tinta" title={rotulo}>
      {rotulo}
    </span>
  );
}

/**
 * Uma barra por item, do zero, em HTML: a maior ocupa a trilha, e as outras
 * são a fração dela. É o que substitui a rosca de seis cores recicladas da
 * "Distribuição por ferramenta" do painel antigo — fatia de pizza não se
 * compara com fatia, e com doze ferramentas a cor se repetia.
 *
 * O número vai na PONTA da barra, e a trilha reserva o espaço dele (7rem):
 * assim as barras de todas as linhas são frações da MESMA largura, e o texto
 * mais comprido ("1.626 · 24,7%") cabe depois da maior. Com o texto numa
 * coluna própria de largura variável, cada linha teria uma trilha de outro
 * tamanho e as barras deixariam de se comparar.
 *
 * A barra é `aria-hidden`: o texto já diz o número, e o leitor de tela lê a
 * lista como lista. No celular o rótulo vai em cima da barra, para a trilha
 * ter a largura inteira.
 *
 * SEM dica, e é EXCEÇÃO registrada à regra da especificação da 000328 ("dica
 * por ponteiro e por teclado em todo gráfico"): a dica existe para dizer o
 * número que o desenho não escreve, e aqui cada linha já escreve o dela, na
 * ponta da barra. Uma dica repetiria a linha que está debaixo do ponteiro, e
 * dar foco a cada linha, sem nada a mais para dizer, só alongaria o caminho de
 * quem navega pelo teclado. Os gráficos do TEMPO têm dica porque lá o número de
 * cada balde não está escrito.
 */
export function BarrasHorizontais({ itens, descricao, dado }: PropsDasBarras) {
  const maximo = Math.max(0, ...itens.map((i) => (Number.isFinite(i.valor) ? i.valor : 0)));
  return (
    <ol className="space-y-2.5 sm:space-y-1" data-grafico={dado} aria-label={descricao}>
      {itens.map((item) => {
        const fracao = maximo > 0 && item.valor > 0 ? item.valor / maximo : 0;
        return (
          <li
            key={item.id}
            className="grid gap-x-3 gap-y-1 sm:grid-cols-[14rem_minmax(0,1fr)] sm:items-center"
            data-barra={item.id}
            data-valor={item.valor}
          >
            <RotuloDaLinha rotulo={item.rotulo} href={item.href} />
            <div className="flex min-w-0 items-center gap-2 py-0.5">
              {fracao > 0 && (
                <span
                  aria-hidden="true"
                  className="block h-3 shrink-0 rounded-r-[4px] bg-acento"
                  // Um valor pequeno ainda aparece (2px): 1 avaliação contra 1.626
                  // daria 0,1px, e a linha pareceria vazia com o número ao lado.
                  style={{ width: `max(2px, calc((100% - 7rem) * ${fracao}))` }}
                />
              )}
              <span className="shrink-0 whitespace-nowrap text-sm tabular-nums text-tinta">{item.texto}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ── Os pontos por linha ─────────────────────────────────────────────────────

export type ItemDosPontos = {
  id: string;
  rotulo: string;
  valor: number | null;
  meta: number | null;
  /** O texto ao lado do ponto: "9,55". */
  texto: string;
  href?: string;
};

export type PropsDosPontos = { itens: ItemDosPontos[]; descricao: string; dado: string };

/**
 * O eixo dos pontos: de `floor(menor valor ou meta − 0,3)` a 10, com marcas de
 * meio ponto quando o eixo tem dois pontos ou menos (senão ele teria três
 * números e as posições não se leriam), de um em um até cinco, e de dois em
 * dois acima disso.
 */
function eixoDosPontos(valores: number[]): { lo: number; ticks: number[] } {
  let lo = valores.length ? Math.max(0, Math.floor(Math.min(...valores) - 0.3)) : 0;
  const passo = 10 - lo <= 2 ? 0.5 : 10 - lo <= 5 ? 1 : 2;
  if (passo === 2 && (10 - lo) % 2 === 1) lo -= 1;
  const ticks: number[] = [];
  for (let t = lo; t <= 10 + 1e-9; t += passo) ticks.push(Math.round(t * 10) / 10);
  return { lo, ticks };
}

/** As três colunas: o rótulo, a trilha e o número. Fixas, para a trilha de todas as linhas ter a mesma largura e os pontos se compararem. */
const GRADE_DOS_PONTOS = "grid grid-cols-[7rem_minmax(0,1fr)_2.75rem] gap-x-3 sm:grid-cols-[14rem_minmax(0,1fr)_3.5rem]";

/**
 * O "gráfico de pontos": uma linha por item, um eixo de notas com ZOOM no alto,
 * o ponto na posição da média e um traço curto vertical na META.
 *
 * Existe no lugar das barras de 0 a 10 do painel antigo: com as médias entre
 * 8,7 e 9,9 (medido em 29/09), barras que começam no zero ficam todas quase
 * cheias e iguais, e a diferença que importa — acima ou abaixo da meta — some.
 * Por isso também NÃO há haste do zero até o ponto: com o eixo em zoom, uma
 * haste mentiria comprimento.
 *
 * As linhas de grade de cada linha se emendam (sem espaço vertical entre as
 * linhas), e formam o fio contínuo de cada marca do eixo.
 *
 * SEM dica, pela mesma exceção das `BarrasHorizontais`: a média de cada linha
 * está escrita na ponta dela, e a meta vai em texto oculto (", meta 9,0" ou ",
 * sem meta") para quem não vê o traço. A dica diria de novo o que a linha já
 * diz.
 */
export function PontosPorLinha({ itens, descricao, dado }: PropsDosPontos) {
  const valores = itens.flatMap((i) => [i.valor, i.meta]).filter((v): v is number => v !== null && Number.isFinite(v));
  const { lo, ticks } = eixoDosPontos(valores);
  const pos = (v: number) => `${Math.min(100, Math.max(0, ((v - lo) / (10 - lo)) * 100))}%`;
  const comMeta = itens.filter((i) => i.meta !== null);
  const metas = [...new Set(comMeta.map((i) => i.meta as number))];
  // A mesma meta em todas (hoje, 9,0 nas vinte) é dita UMA vez, na legenda:
  // "meta 9,0" repetido em cada linha seria a mesma informação doze vezes. E
  // "para todas" só quando TODAS as linhas do gráfico têm meta: até 29/09 bastava
  // que as que tinham meta concordassem, e a frase era verdadeira por acaso —
  // um «Tirar a meta» numa ferramenta avaliada no período a deixaria afirmando a
  // meta de uma linha sem traço nenhum. Com uma sem meta, a legenda diz "a meta
  // de cada uma", e cada linha diz a sua (ou "sem meta") ao leitor de tela.
  const metaComum = metas.length === 1 && comMeta.length >= 2 && comMeta.length === itens.length ? metas[0] : null;
  const numeroDoEixo = (t: number) => t.toLocaleString("pt-BR", { maximumFractionDigits: 1 });

  return (
    <div data-grafico={dado} data-dominio={`${lo}..10`}>
      <p className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-cinza" data-legenda-dos-pontos>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-2.5 shrink-0 rounded-full bg-acento" />a média no período
        </span>
        {metas.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="h-3.5 w-0.5 shrink-0 rounded-full bg-cinza" />
            {metaComum !== null ? `a meta, ${metaLegivel(metaComum)} para todas` : "a meta de cada uma"}
          </span>
        )}
        {lo > 0 && <span>O eixo começa em {numeroDoEixo(lo)}, e não no zero, para as diferenças aparecerem.</span>}
      </p>

      {/* O eixo, com os números no alto. Só para o olho: cada linha diz o seu número em texto. */}
      <div aria-hidden="true" className={GRADE_DOS_PONTOS}>
        <span />
        <div className="relative h-5">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute bottom-0.5 -translate-x-1/2 text-xs tabular-nums text-cinza"
              style={{ left: pos(t) }}
              data-marca-do-eixo={t}
            >
              {numeroDoEixo(t)}
            </span>
          ))}
        </div>
        <span />
      </div>

      <ol aria-label={descricao}>
        {itens.map((item) => (
          <li
            key={item.id}
            className={`${GRADE_DOS_PONTOS} items-center`}
            data-ponto={item.id}
            data-valor={item.valor ?? ""}
            data-meta={item.meta ?? ""}
          >
            <span className="min-w-0 py-1">
              <RotuloDaLinha rotulo={item.rotulo} href={item.href} />
            </span>
            <div aria-hidden="true" className="relative min-h-7 self-stretch">
              {ticks.map((t) => (
                <span key={t} className="absolute inset-y-0 w-px bg-borda" style={{ left: pos(t) }} />
              ))}
              {/* O traço da meta é mais alto que o ponto: quando os dois caem no
                  mesmo lugar, as pontas do traço continuam à vista. */}
              {item.meta !== null && (
                <span
                  className="absolute top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cinza"
                  style={{ left: pos(item.meta) }}
                  data-traco-da-meta
                />
              )}
              {item.valor !== null && (
                <span
                  className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-acento ring-2 ring-superficie"
                  style={{ left: pos(item.valor) }}
                  data-marca-do-ponto
                />
              )}
            </div>
            <span className="text-right text-sm font-semibold tabular-nums text-titulo">
              {item.texto}
              {/* A meta de cada linha, para quem não vê o traço. */}
              <span className="sr-only">{item.meta !== null ? `, meta ${metaLegivel(item.meta)}` : ", sem meta"}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
